import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Box, Braces, CheckCircle2, Columns3, Edit3, Plug, Plus, Power, RefreshCw, Server, Trash2 } from 'lucide-react';
import { adminApi, customAdminApi } from '../../../services/api';
import type { AdminHostAgent } from '../../../types/admin';
import type { CustomProduct, CustomProductRevision } from '../../../types/custom';
import { customErrorMessage, formatBytes } from '../../../utils/customUi';
import { latestCustomProductRevision, selectedCustomProductRevision, type CustomRevisionSelection } from '../../../utils/customProductRevisions';
import { CustomImagesPanel } from './CustomImagesPanel';
import { CustomInstancesPanel } from './CustomInstancesPanel';
import { CustomIntegrationPanel } from './CustomIntegrationPanel';
import { CustomProductBoardPanel } from './CustomProductBoardPanel';
import { ProductMetadataModal, RevisionEditorModal } from './CustomProductEditors';

type Section = 'definition' | 'images' | 'instances' | 'integration' | 'board';

function productSection(value?: string): Section {
  return ['definition', 'images', 'instances', 'integration', 'board'].includes(value || '')
    ? value as Section
    : 'definition';
}

export function CustomProductsManager({
  initialProductId,
  initialSection,
  onNavigationChange,
}: {
  initialProductId?: string;
  initialSection?: string;
  onNavigationChange?: (productId: string | null, section: Section) => void;
} = {}) {
  const queryClient = useQueryClient();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(initialProductId || null);
  const [section, setSection] = useState<Section>(productSection(initialSection));
  const [showProductEditor, setShowProductEditor] = useState<'create' | 'edit' | null>(null);
  const [showRevisionEditor, setShowRevisionEditor] = useState(false);
  const [baseRevision, setBaseRevision] = useState<CustomProductRevision | undefined>();
  const [revisionSelection, setRevisionSelection] = useState<CustomRevisionSelection | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const productsQuery = useQuery({
    queryKey: ['custom-products'],
    queryFn: async () => (await customAdminApi.listProducts()).products,
    refetchInterval: 10_000,
  });
  const agentsQuery = useQuery({
    queryKey: ['host-agents', 'custom-picker'],
    queryFn: async () => {
      const response = await adminApi.getHostAgents({ page: 1, pageSize: 100 }) as { hostAgents: AdminHostAgent[] };
      return response.hostAgents;
    },
    refetchInterval: 5_000,
  });
  const detailQuery = useQuery({
    queryKey: ['custom-products', selectedProductId, 'detail'],
    queryFn: async () => customAdminApi.getProduct(selectedProductId!),
    enabled: Boolean(selectedProductId),
    refetchInterval: 10_000,
  });

  const products = useMemo(() => productsQuery.data || [], [productsQuery.data]);
  const product = detailQuery.data?.product;
  const revisions = detailQuery.data?.revisions || [];
  const latestRevision = latestCustomProductRevision(revisions);
  const agents = agentsQuery.data || [];

  const setProductNavigation = useCallback((productId: string | null, nextSection: Section) => {
    setSelectedProductId(productId);
    setSection(nextSection);
    onNavigationChange?.(productId, nextSection);
  }, [onNavigationChange]);

  useEffect(() => {
    setSelectedProductId(initialProductId || null);
    setSection(productSection(initialSection));
  }, [initialProductId, initialSection]);

  useEffect(() => {
    if (!selectedProductId && products[0]) setProductNavigation(products[0].id, section);
    if (selectedProductId && products.length && !products.some(item => item.id === selectedProductId)) {
      setProductNavigation(products[0]?.id || null, section);
    }
  }, [products, section, selectedProductId, setProductNavigation]);

  useEffect(() => {
    setRevisionSelection(null);
  }, [product?.id]);

  const selectedRevisionNumber = selectedCustomProductRevision(product?.id || '', revisions, revisionSelection);
  const selectedRevision = revisions.find(revision => revision.revision === selectedRevisionNumber);
  const selectRevision = (revision: number) => {
    if (product) setRevisionSelection({ productId: product.id, latestRevision, revision });
  };

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['custom-products'] }),
      queryClient.invalidateQueries({ queryKey: ['custom-products', selectedProductId] }),
      queryClient.invalidateQueries({ queryKey: ['custom-instances'] }),
    ]);
  };

  const productAction = async (action: 'activate' | 'disable' | 'delete') => {
    if (!product) return;
    setBusy(action);
    setError('');
    try {
      if (action === 'activate') {
        if (!latestRevision) throw new Error('Create a revision before activating the product.');
        await customAdminApi.activateProduct(product.id);
        setNotice('Product activated. New instances can use its available image releases.');
      } else if (action === 'disable') {
        await customAdminApi.disableProduct(product.id);
        setNotice('Product disabled. Existing pinned images and instances remain unchanged.');
      } else {
        await customAdminApi.deleteProduct(product.id);
        setNotice('Empty draft product deleted.');
        setProductNavigation(null, 'definition');
      }
      await refresh();
    } catch (caught) {
      setError(customErrorMessage(caught, `The product could not be ${action}d.`));
    } finally {
      setBusy(null);
    }
  };

  const eligibleAgentCount = agents.filter(agent => agent.isActive && agent.connection.connected && agent.customWorkloadsEnabled).length;
  return <div className="space-y-5">
    <div className="flex flex-wrap justify-between items-start gap-4">
      <div><h2 className="section-title">Custom Products</h2><p className="section-subtitle mt-1">Admin-only product → immutable image release/Agent replica → pinned instance. Customer GameServers stay separate.</p></div>
      <div className="flex gap-2"><button className="btn-secondary" onClick={() => void refresh()}><RefreshCw className="w-4 h-4" /> Refresh</button><button className="btn-primary" onClick={() => setShowProductEditor('create')}><Plus className="w-4 h-4" /> New product</button></div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Products" value={products.length} />
      <Stat label="Active" value={products.filter(item => item.status === 'active').length} />
      <Stat label="Custom Agents ready" value={eligibleAgentCount} attention={eligibleAgentCount === 0} />
      <Stat label="Distribution" value="Local replicas" />
    </div>
    {error && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300">{error}</div>}
    {notice && <div className="p-3 rounded border border-neon-500/20 bg-neon-500/10 text-sm text-neon-300">{notice}</div>}

    <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-5 items-start">
      <aside className="card overflow-hidden lg:sticky lg:top-4">
        <div className="px-4 py-3 border-b border-dark-600 text-xs font-semibold uppercase tracking-wider text-gray-500">Product catalog</div>
        {productsQuery.isLoading && <div className="p-8"><div className="spinner w-7 h-7 mx-auto" /></div>}
        {productsQuery.isError && <div className="p-4 text-sm text-red-300">{customErrorMessage(productsQuery.error, 'Products could not be loaded.')}</div>}
        {!productsQuery.isLoading && products.length === 0 && <div className="p-6 text-center text-sm text-gray-500">No products. Create the first declarative definition.</div>}
        <div className="divide-y divide-dark-700">{products.map(item => <button key={item.id} className={`w-full text-left p-4 transition-colors ${selectedProductId === item.id ? 'bg-neon-500/10 border-l-2 border-neon-500' : 'hover:bg-dark-700/50 border-l-2 border-transparent'}`} onClick={() => setProductNavigation(item.id, 'definition')}><div className="flex justify-between gap-2"><span className="font-medium text-gray-200 truncate">{item.displayName}</span><ProductStatus status={item.status} /></div><div className="text-xs text-gray-500 mt-1">{item.key} · r{item.latestRevision || '—'}</div></button>)}</div>
      </aside>

      <main className="min-w-0">
        {detailQuery.isLoading && <div className="card p-16"><div className="spinner w-10 h-10 mx-auto" /></div>}
        {detailQuery.isError && <div className="card p-5 text-red-300">{customErrorMessage(detailQuery.error, 'Product detail could not be loaded.')}</div>}
        {!selectedProductId && !productsQuery.isLoading && <div className="card empty-state"><Braces className="empty-state-icon" /><div className="empty-state-title">Select or create a product</div><p className="empty-state-text">Definitions are declarative and do not require Agent code changes.</p></div>}
        {product && <div className="space-y-4">
          <div className="card p-5">
            <div className="flex flex-wrap justify-between gap-4">
              <div><div className="flex items-center gap-2"><h3 className="text-xl font-semibold text-gray-100">{product.displayName}</h3><ProductStatus status={product.status} /></div><div className="text-xs text-gray-500 mt-1">{product.key} · latest r{latestRevision || '—'}</div>{product.description && <p className="text-sm text-gray-400 mt-3 max-w-3xl">{product.description}</p>}</div>
              <div className="flex flex-wrap items-start gap-2"><button className="btn-secondary" onClick={() => setShowProductEditor('edit')}><Edit3 className="w-4 h-4" /> Metadata</button><button className="btn-secondary" onClick={() => { setBaseRevision(selectedRevision); setShowRevisionEditor(true); }}><Plus className="w-4 h-4" /> Revision</button>{product.status !== 'active' && <button className="btn-primary" disabled={!latestRevision || Boolean(busy)} onClick={() => void productAction('activate')}><CheckCircle2 className="w-4 h-4" /> Activate product</button>}{product.status === 'active' && <button className="btn-secondary" disabled={Boolean(busy)} onClick={() => void productAction('disable')}><Power className="w-4 h-4" /> Disable product</button>}{product.status === 'draft' && product.latestRevision === 0 && <button className="btn-danger" disabled={Boolean(busy)} onClick={() => confirm(`Delete empty draft ${product.displayName}?`) && void productAction('delete')}><Trash2 className="w-4 h-4" /></button>}</div>
            </div>
          </div>

          <div className="flex gap-2 border-b border-dark-700 overflow-x-auto">
            <SectionButton active={section === 'definition'} onClick={() => setProductNavigation(selectedProductId, 'definition')} icon={<Braces className="w-4 h-4" />} label="Definition & revisions" />
            <SectionButton active={section === 'images'} onClick={() => setProductNavigation(selectedProductId, 'images')} icon={<Box className="w-4 h-4" />} label="Images & replicas" />
            <SectionButton active={section === 'instances'} onClick={() => setProductNavigation(selectedProductId, 'instances')} icon={<Server className="w-4 h-4" />} label="Instances" />
            <SectionButton active={section === 'integration'} onClick={() => setProductNavigation(selectedProductId, 'integration')} icon={<Plug className="w-4 h-4" />} label="Integration" />
            <SectionButton active={section === 'board'} onClick={() => setProductNavigation(selectedProductId, 'board')} icon={<Columns3 className="w-4 h-4" />} label="Board" />
          </div>

          {section === 'definition' && <DefinitionPanel revisions={revisions} selected={selectedRevision} onSelect={selectRevision} onNewRevision={revision => { setBaseRevision(revision); setShowRevisionEditor(true); }} />}
          {section === 'images' && <CustomImagesPanel key={product.id} product={product} revisions={revisions} agents={agents} />}
          {section === 'instances' && <CustomInstancesPanel product={product} revisions={revisions} agents={agents} />}
          {section === 'integration' && product.id === selectedProductId && <CustomIntegrationPanel key={product.id} product={product} />}
          {section === 'board' && product.id === selectedProductId && <CustomProductBoardPanel key={product.id} product={product} />}
        </div>}
      </main>
    </div>

    {showProductEditor && <ProductMetadataModal product={showProductEditor === 'edit' ? product : undefined} onClose={() => setShowProductEditor(null)} onSaved={async saved => { setShowProductEditor(null); setProductNavigation(saved.id, 'definition'); setNotice(`Product ${saved.displayName} saved.`); await refresh(); }} />}
    {showRevisionEditor && product && <RevisionEditorModal product={product} baseRevision={baseRevision} onClose={() => setShowRevisionEditor(false)} onSaved={async revision => { setShowRevisionEditor(false); setRevisionSelection(null); setNotice(`Immutable revision ${revision.revision} created.`); await refresh(); }} />}
  </div>;
}

function DefinitionPanel({
  revisions,
  selected,
  onSelect,
  onNewRevision,
}: {
  revisions: CustomProductRevision[];
  selected?: CustomProductRevision;
  onSelect: (revision: number) => void;
  onNewRevision: (revision?: CustomProductRevision) => void;
}) {
  const definition = selected?.definition;
  if (!definition) return <div className="card empty-state py-12"><Braces className="empty-state-icon" /><div className="empty-state-title">No product revision</div><p className="empty-state-text">Create a generic schema before uploading an image.</p><button className="btn-primary" onClick={() => onNewRevision()}><Plus className="w-4 h-4" /> Create first revision</button></div>;
  return <div className="space-y-4">
    <div className="card p-4 flex flex-wrap gap-3 items-end">
      <label className="text-xs text-gray-500 min-w-64">Inspect revision<select className="input mt-1" value={selected.revision} onChange={event => onSelect(Number(event.target.value))}>{revisions.map(revision => <option key={revision.id} value={revision.revision}>r{revision.revision} · {revision.contentHash.slice(0, 12)}</option>)}</select></label>
      <button className="btn-secondary" onClick={() => onNewRevision(selected)}><Archive className="w-4 h-4" /> Clone into new revision</button>
      <div className="ml-auto text-xs text-gray-500">created {new Date(selected.createdAt).toLocaleString()}</div>
    </div>
    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
      <SummaryCard title="Artifact" lines={[`${definition.artifactContract.format.toUpperCase()} · ${definition.artifactContract.requiredPaths.length} required path(s)`, definition.artifactContract.requiredPaths.join(', ')]} />
      <SummaryCard title="Launch" lines={[`${definition.launch.executable} + ${definition.launch.fixedArgs.length} fixed arg(s)`, `${definition.launch.parameters.length} typed parameter(s) · ${definition.launch.environment.length} env`]} />
      <SummaryCard title="Network & data" lines={[`${definition.launch.ports.length} port(s) · ${definition.launch.volumes.length} volume(s)`, `${definition.launch.restartPolicy} · stop ${definition.launch.stopTimeoutSeconds}s`]} />
      <SummaryCard title="Profiles" lines={definition.resourcePolicy.profiles.map(profile => `${profile.label}: ${profile.cpuCores} CPU · ${formatBytes(profile.memoryBytes)} · ${formatBytes(profile.storageBytes)}`)} />
    </div>
    <div className="grid xl:grid-cols-2 gap-4">
      <div className="card p-4"><h4 className="font-medium text-gray-200">Schema-driven instance fields</h4><div className="mt-3 space-y-2">{definition.launch.parameters.length ? definition.launch.parameters.map(parameter => <div key={parameter.key} className="rounded border border-dark-600 p-3 flex justify-between gap-3"><div><div className="text-sm text-gray-300">{parameter.label}{parameter.required ? ' *' : ''}</div><div className="text-xs text-gray-500 mt-1">{parameter.key} · {parameter.type}{parameter.default !== undefined ? ` · default ${String(parameter.default)}` : ''}</div></div><code className="text-xs text-gray-600">{parameter.cli.flag || `position ${parameter.cli.position}`}</code></div>) : <div className="text-sm text-gray-500">No runtime parameters.</div>}</div></div>
      <details className="card p-4"><summary className="font-medium text-cyber-400 cursor-pointer">Full immutable JSON definition</summary><pre className="mt-3 max-h-[34rem] overflow-auto rounded bg-black/40 p-4 text-xs text-gray-400">{JSON.stringify(definition, null, 2)}</pre></details>
    </div>
  </div>;
}

function Stat({ label, value, attention = false }: { label: string; value: string | number; attention?: boolean }) {
  return <div className="card p-4"><div className={`text-xl font-semibold ${attention ? 'text-amber-400' : 'text-gray-100'}`}>{value}</div><div className="text-xs text-gray-500 mt-1">{label}</div></div>;
}

function ProductStatus({ status }: { status: CustomProduct['status'] }) {
  return <span className={status === 'active' ? 'badge-online' : (status === 'disabled' ? 'badge-error' : 'badge-warning')}>{status}</span>;
}

function SectionButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return <button className={active ? 'tab-active' : 'tab'} onClick={onClick}>{icon}{label}</button>;
}

function SummaryCard({ title, lines }: { title: string; lines: string[] }) {
  return <div className="card p-4"><div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</div>{lines.map((line, index) => <div key={index} className={`text-xs mt-2 break-words ${index === 0 ? 'text-gray-300' : 'text-gray-500'}`}>{line || '—'}</div>)}</div>;
}
